(() => {
  if (customElements.get('zapped-logo-generator')) return;
  class LogoGenerator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      this.status = this.querySelector('.zl-status');
      this.submit = this.querySelector('[type=submit]');
      this.reference = this.querySelector('[name=reference]');
      this.form.addEventListener('submit', e => this.generate(e));
      this.reference.addEventListener('change', () => this.selectReference());
      this.querySelector('.zl-remove').addEventListener('click', () => this.clearReference());
      if (this.dataset.endpoint && this.dataset.sitekey) this.initVerification();
    }
    disconnectedCallback() {
      this.controller?.abort();
      if (this.widget !== undefined && window.turnstile) window.turnstile.remove(this.widget);
      this.widget = undefined;
      this.initialized = false;
      for (const url of [this.referenceUrl, this.resultUrl]) if (url) URL.revokeObjectURL(url);
    }
    message(key) { return this.dataset['msg' + key.split('_').map(x => x[0].toUpperCase()+x.slice(1)).join('')]; }
    say(key) { this.status.textContent = this.message(key); }
    async initVerification() {
      try {
        if (!window.zappedTurnstileReady) {
          window.zappedTurnstileReady = new Promise((resolve, reject) => {
            if (window.turnstile) return resolve();
            window.zappedTurnstileLoaded = resolve;
            const script = document.createElement('script');
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=zappedTurnstileLoaded';
            script.async = true;
            script.onerror = reject;
            document.head.append(script);
          });
        }
        await window.zappedTurnstileReady;
        if (!this.isConnected) return;
        this.widget = window.turnstile.render(this.querySelector('.zl-verification'), {
          sitekey: this.dataset.sitekey, action: 'logo_generate',
          callback: token => { this.token = token; },
          'expired-callback': () => { this.token = ''; },
          'error-callback': () => { this.token = ''; this.say('human_error'); }
        });
      } catch { this.say('human_error'); }
    }
    clearReference() {
      this.reference.value = '';
      if (this.referenceUrl) URL.revokeObjectURL(this.referenceUrl);
      this.referenceUrl = '';
      this.querySelector('.zl-reference').hidden = true;
      this.querySelector('.zl-reference img').removeAttribute('src');
    }
    async selectReference() {
      const file = this.reference.files[0];
      if (!file) return this.clearReference();
      if (!['image/png','image/jpeg','image/webp'].includes(file.type) || file.size > 5*1024*1024 || !file.size) {
        this.clearReference(); return this.say('badfile');
      }
      let url;
      try {
        url = URL.createObjectURL(file);
        const test = new Image(); test.src = url; await test.decode();
        if (test.naturalWidth * test.naturalHeight > 40000000) throw new Error('Too large');
        if (this.reference.files[0] !== file) { URL.revokeObjectURL(url); return; }
        if (this.referenceUrl) URL.revokeObjectURL(this.referenceUrl);
        this.referenceUrl = url;
        this.querySelector('.zl-reference img').src = url;
        this.querySelector('.zl-reference span').textContent = file.name;
        this.querySelector('.zl-reference').hidden = false;
        this.status.textContent = '';
      } catch { if (url) URL.revokeObjectURL(url); this.clearReference(); this.say('badimage'); }
    }
    async monochrome(blob) {
      const url = URL.createObjectURL(blob);
      try {
        const img = new Image(); img.src = url; await img.decode();
        const canvas = document.createElement('canvas'); canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d'); ctx.drawImage(img, 0, 0);
        const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
        for (let i=0; i<frame.data.length; i+=4) {
          const value = (.2126*frame.data[i]+.7152*frame.data[i+1]+.0722*frame.data[i+2]) >= 128 ? 255 : 0;
          frame.data[i] = frame.data[i+1] = frame.data[i+2] = value;
        }
        ctx.putImageData(frame, 0, 0);
        return await new Promise((resolve,reject) => canvas.toBlob(b => b ? resolve(b) : reject(new Error()), 'image/png'));
      } finally { URL.revokeObjectURL(url); }
    }
    async generate(event) {
      event.preventDefault();
      if (this.submit.disabled || !this.form.reportValidity()) return;
      if (this.dataset.preview === 'true') return this.say('preview_mode');
      if (!this.dataset.endpoint || !this.dataset.sitekey) return this.say('setup');
      if (!this.token) return this.say('verify');
      const data = new FormData(this.form);
      const mode = data.get('mode');
      if (!this.reference.files[0]) data.delete('reference');
      data.set('token', this.token);
      this.controller = new AbortController();
      const timer = setTimeout(() => this.controller.abort(), 180000);
      this.submit.disabled = true; this.classList.add('busy'); this.form.setAttribute('aria-busy','true'); this.say('working');
      try {
        const response = await fetch(this.dataset.endpoint, {method:'POST',body:data,signal:this.controller.signal,credentials:'omit'});
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || this.message('retry'));
        }
        if (!response.headers.get('content-type')?.startsWith('image/png')) throw new Error(this.message('retry'));
        let blob = await response.blob();
        if (mode === 'bw') blob = await this.monochrome(blob);
        if (this.resultUrl) URL.revokeObjectURL(this.resultUrl);
        this.resultUrl = URL.createObjectURL(blob);
        const output = this.querySelector('.zl-output'); output.src = this.resultUrl; await output.decode(); output.hidden = false;
        this.querySelector('.zl-empty').hidden = true;
        this.querySelector('.zl-actions').hidden = false;
        const download = this.querySelector('.zl-download'); download.href = this.resultUrl;
        download.download = 'zapped-logo-' + (mode === 'bw' ? 'black-white' : 'full-color') + '.png';
        this.say('success');
      } catch (error) {
        if (error.name === 'AbortError') this.say('timeout'); else this.status.textContent = error.message || this.message('retry');
      } finally {
        clearTimeout(timer); this.submit.disabled = false; this.classList.remove('busy'); this.form.removeAttribute('aria-busy');
        this.token = ''; if (this.widget !== undefined && window.turnstile) window.turnstile.reset(this.widget);
      }
    }
  }
  customElements.define('zapped-logo-generator', LogoGenerator);
})();
